import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axiosClient from '../axiosClient';
import { useStateContext } from '../context/ContextProvider';

function AppointmentListView() {
  const { t } = useTranslation();
  const { user } = useStateContext();
  const [earlierAppointments, setEarlierAppointments] = useState([]);
  const [regularAppointments, setRegularAppointments] = useState([]);
  const [earlierPage, setEarlierPage] = useState(0);
  const [regularPage, setRegularPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Default to 10
  const [earlierTotalCount, setEarlierTotalCount] = useState(0);
  const [regularTotalCount, setRegularTotalCount] = useState(0);
  const [daysFilter, setDaysFilter] = useState('');
  const [earlierNextLink, setEarlierNextLink] = useState(null);
  const [earlierPreviousLink, setEarlierPreviousLink] = useState(null);
  const [regularNextLink, setRegularNextLink] = useState(null);
  const [regularPreviousLink, setRegularPreviousLink] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState({ email: false, sms: false });
  const [smsLanguage, setSmsLanguage] = useState('en'); // Language for bulk SMS (en or el)

  const fetchEarlierAppointments = useCallback(
    async (url = `/api/earlier-appointments/?page=${earlierPage + 1}&days=${daysFilter}&page_size=${rowsPerPage}`) => {
      try {
        console.log('Fetching earlier appointments from:', url);
        const response = await axiosClient.get(url);
        setEarlierAppointments(response.data.results || []);
        setEarlierTotalCount(response.data.count || 0);
        setEarlierNextLink(response.data.next);
        setEarlierPreviousLink(response.data.previous);
        setError(null);
        console.log('Earlier pagination state:', {
          page: earlierPage,
          rowsPerPage,
          totalCount: response.data.count,
          resultsCount: response.data.results.length,
        });
      } catch (error) {
        console.error('Error fetching earlier appointments:', error);
        setError(`Failed to fetch earlier appointments: ${error.message}`);
      }
    },
    [earlierPage, daysFilter, rowsPerPage]
  );

  const fetchRegularAppointments = useCallback(
    async (url = `/api/regular-appointments/?page=${regularPage + 1}&days=${daysFilter}&page_size=${rowsPerPage}`) => {
      try {
        console.log('Fetching regular appointments from:', url);
        const response = await axiosClient.get(url);
        setRegularAppointments(response.data.results || []);
        setRegularTotalCount(response.data.count || 0);
        setRegularNextLink(response.data.next);
        setRegularPreviousLink(response.data.previous);
        setError(null);
        console.log('Regular pagination state:', {
          page: regularPage,
          rowsPerPage,
          totalCount: response.data.count,
          resultsCount: response.data.results.length,
        });
      } catch (error) {
        console.error('Error fetching regular appointments:', error);
        setError(`Failed to fetch regular appointments: ${error.message}`);
      }
    },
    [regularPage, daysFilter, rowsPerPage]
  );

  useEffect(() => {
    if (user?.user_role === 'STAFF' || user?.user_role === 'ADMIN') {
      fetchEarlierAppointments();
      fetchRegularAppointments();
    }
  }, [user, fetchEarlierAppointments, fetchRegularAppointments]);

  const handleEarlierPageChange = (event, newPage) => {
    const maxEarlierPage = Math.ceil(earlierTotalCount / rowsPerPage) - 1;
    if (newPage < 0 || newPage > maxEarlierPage) return;

    if (newPage > earlierPage && earlierNextLink) {
      console.log('Navigating to earlier next link:', earlierNextLink);
      fetchEarlierAppointments(earlierNextLink);
      setEarlierPage(newPage);
    } else if (newPage < earlierPage && earlierPreviousLink) {
      console.log('Navigating to earlier previous link:', earlierPreviousLink);
      fetchEarlierAppointments(earlierPreviousLink);
      setEarlierPage(newPage);
    } else {
      const url = `/api/earlier-appointments/?page=${newPage + 1}&days=${daysFilter}&page_size=${rowsPerPage}`;
      console.log('Fallback URL for earlier:', url);
      fetchEarlierAppointments(url);
      setEarlierPage(newPage);
    }
  };

  const handleRegularPageChange = (event, newPage) => {
    const maxRegularPage = Math.ceil(regularTotalCount / rowsPerPage) - 1;
    if (newPage < 0 || newPage > maxRegularPage) return;

    if (newPage > regularPage && regularNextLink) {
      console.log('Navigating to regular next link:', regularNextLink);
      fetchRegularAppointments(regularNextLink);
      setRegularPage(newPage);
    } else if (newPage < regularPage && regularPreviousLink) {
      console.log('Navigating to regular previous link:', regularPreviousLink);
      fetchRegularAppointments(regularPreviousLink);
      setRegularPage(newPage);
    } else {
      const url = `/api/regular-appointments/?page=${newPage + 1}&days=${daysFilter}&page_size=${rowsPerPage}`;
      console.log('Fallback URL for regular:', url);
      fetchRegularAppointments(url);
      setRegularPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setEarlierPage(0);
    setRegularPage(0);
    setEarlierNextLink(null);
    setEarlierPreviousLink(null);
    setRegularNextLink(null);
    setRegularPreviousLink(null);
  };

  const handleDaysFilterChange = (event) => {
    setDaysFilter(event.target.value);
    setEarlierPage(0);
    setRegularPage(0);
    setEarlierNextLink(null);
    setEarlierPreviousLink(null);
    setRegularNextLink(null);
    setRegularPreviousLink(null);
    setError(null);
    setSuccessMessage(null);
  };

  const clearFilter = () => {
    setDaysFilter('');
    setEarlierPage(0);
    setRegularPage(0);
    setEarlierNextLink(null);
    setEarlierPreviousLink(null);
    setRegularNextLink(null);
    setRegularPreviousLink(null);
    setError(null);
    setSuccessMessage(null);
  };

  // Handler for sending bulk emails
  const handleSendBulkEmails = async () => {
    if (user?.user_role !== 'STAFF' && user?.user_role !== 'ADMIN') {
      setError(t('You do not have permission to send bulk emails'));
      return;
    }
    setLoading((prev) => ({ ...prev, email: true }));
    setError(null);
    setSuccessMessage(null);
    try {
      const payload = daysFilter ? { days: [parseInt(daysFilter)] } : { days: 'all' };
      const response = await axiosClient.post('/api/send-bulk-emails/', payload);
      if (response.status === 207) {
        setError(
          t('Some emails failed to send: ') +
          response.data.errors.join(', ')
        );
        setSuccessMessage(t('Bulk emails processed with some errors'));
      } else {
        setSuccessMessage(t('Bulk emails sent successfully'));
      }
    } catch (error) {
      setError(t('Failed to send bulk emails: ') + error.message);
    } finally {
      setLoading((prev) => ({ ...prev, email: false }));
    }
  };

  // Handler for sending bulk SMS
  const handleSendBulkSMS = async () => {
    if (user?.user_role !== 'STAFF' && user?.user_role !== 'ADMIN') {
      setError(t('You do not have permission to send bulk SMS'));
      return;
    }
    setLoading((prev) => ({ ...prev, sms: true }));
    setError(null);
    setSuccessMessage(null);
    try {
      const payload = daysFilter
        ? { days: [parseInt(daysFilter)], language: smsLanguage }
        : { days: 'all', language: smsLanguage };
      const response = await axiosClient.post('/api/send-bulk-sms/', payload);
      if (response.status === 207) {
        setError(
          t('Some SMS failed to send: ') + response.data.errors.join(', ')
        );
        setSuccessMessage(t('Bulk SMS processed with some errors'));
      } else {
        setSuccessMessage(t('Bulk SMS sent successfully'));
      }
    } catch (error) {
      setError(t('Failed to send bulk SMS: ') + error.message);
    } finally {
      setLoading((prev) => ({ ...prev, sms: false }));
    }
  };

  const renderAppointmentTable = (appointments, title, page, totalCount, onPageChange) => {
    // Calculate effective rows per page for display
    const effectiveRowsPerPage = Math.min(rowsPerPage, totalCount);
    const showPagination = totalCount > effectiveRowsPerPage;

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t(title)}
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('Patient Name')}</TableCell>
                <TableCell>{t('Doctor Name')}</TableCell>
                <TableCell>{t('Appointment Date')}</TableCell>
                <TableCell>{t('Start Time')}</TableCell>
                <TableCell>{t('End Time')}</TableCell>
                <TableCell>{t('PatientInformed')}</TableCell>
                <TableCell>{t('PatientArrival')}</TableCell>
                <TableCell>{t('EarlierDate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.map((appointment) => (
                <TableRow key={appointment.id}>
                  <TableCell>{`${appointment.client_name} ${appointment.client_surname}`}</TableCell>
                  <TableCell>{`${appointment.staff_name} ${appointment.staff_surname}`}</TableCell>
                  <TableCell>{appointment.appointment_date}</TableCell>
                  <TableCell>{appointment.appointment_start}</TableCell>
                  <TableCell>{appointment.appointment_end}</TableCell>
                  <TableCell>{appointment.patient_informed ? t('Yes') : t('No')}</TableCell>
                  <TableCell>{appointment.patient_arival ? t('Yes') : t('No')}</TableCell>
                  <TableCell>{appointment.earlier_date || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {showPagination ? (
          <TablePagination
            rowsPerPageOptions={[1, 5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ page }) => `Page: ${page + 1} of ${Math.ceil(totalCount / rowsPerPage)}`}
          />
        ) : (
          <Typography sx={{ p: 1 }}>
            {t('Showing')} {totalCount} {t('of')} {totalCount} {t('rows')}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('Appointment List')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {(user?.user_role === 'STAFF' || user?.user_role === 'ADMIN') && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'center',
            mt: 3,
            mb: 4,
            gap: 2,
          }}
        >
          <FormControl sx={{ minWidth: 150, mr: { sm: 2 }, mb: { xs: 2, sm: 0 } }}>
            <InputLabel>{t('Filter by Days')}</InputLabel>
            <Select
              value={daysFilter}
              onChange={handleDaysFilterChange}
              label={t('Filter by Days')}
            >
              <MenuItem value="">{t('All Future Appointments')}</MenuItem>
              <MenuItem value="1">{t('Next 1 Day')}</MenuItem>
              <MenuItem value="2">{t('Next 2 Days')}</MenuItem>
              <MenuItem value="3">{t('Next 3 Days')}</MenuItem>
              <MenuItem value="4">{t('Next 4 Days')}</MenuItem>
              <MenuItem value="5">{t('Next 5 Days')}</MenuItem>
              <MenuItem value="6">{t('Next 6 Days')}</MenuItem>
              <MenuItem value="7">{t('Next 7 Days')}</MenuItem>
              <MenuItem value="30">{t('Next 30 Days')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            onClick={handleSendBulkEmails}
            variant="contained"
            disabled={loading.email}
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            {loading.email ? <CircularProgress size={24} color="inherit" /> : t('Send Bulk Emails')}
          </Button>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('SMS Language')}</InputLabel>
            <Select
              value={smsLanguage}
              label={t('SMS Language')}
              onChange={(e) => setSmsLanguage(e.target.value)}
            >
              <MenuItem value="en">{t('English')}</MenuItem>
              <MenuItem value="el">{t('Greek')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            onClick={handleSendBulkSMS}
            variant="contained"
            disabled={loading.sms}
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              backgroundColor: 'secondary.main',
              '&:hover': { backgroundColor: 'secondary.dark' },
            }}
          >
            {loading.sms ? <CircularProgress size={24} color="inherit" /> : t('Send Bulk SMS')}
          </Button>
          <Button
            onClick={clearFilter}
            variant="outlined"
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                borderColor: 'primary.main',
                color: 'primary.contrastText',
              },
            }}
          >
            {t('Clear Filter')}
          </Button>
        </Box>
      )}
      {renderAppointmentTable(
        earlierAppointments,
        'Earlier Appointments',
        earlierPage,
        earlierTotalCount,
        handleEarlierPageChange
      )}
      {renderAppointmentTable(
        regularAppointments,
        'Regular Appointments',
        regularPage,
        regularTotalCount,
        handleRegularPageChange
      )}
    </Box>
  );
}

export default AppointmentListView;